import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, API } from "../App";
import { useCoPilot } from "./CoPilot";
import { Lightbulb, X, ArrowRight, Sparkles, AlertTriangle, Info, CheckCircle2 } from "lucide-react";

const severityIcon = {
  warning: AlertTriangle,
  info: Info,
  success: CheckCircle2,
};

const severityColor = {
  warning: "text-amber-400",
  info: "text-blue-400",
  success: "text-emerald-400",
};

const CoPilotInsights = () => {
  const { user } = useAuth();
  const copilot = useCoPilot();
  const navigate = useNavigate();
  const [nudges, setNudges] = useState([]);
  const [open, setOpen] = useState(false);
  const [dismissing, setDismissing] = useState(new Set());

  const fetchNudges = useCallback(async () => {
    if (!user) return;
    try {
      const r = await fetch(`${API}/copilot/nudges`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setNudges(d.nudges || []);
      }
    } catch {}
  }, [user]);

  // Fetch on mount + every 5 minutes
  useEffect(() => {
    fetchNudges();
    const id = setInterval(fetchNudges, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [fetchNudges]);

  const dismiss = async (e, nudgeId) => {
    e.stopPropagation();
    setDismissing(prev => new Set(prev).add(nudgeId));
    try {
      await fetch(`${API}/copilot/nudges/dismiss`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ nudge_id: nudgeId }),
      });
      setTimeout(() => {
        setNudges(prev => prev.filter(n => n.id !== nudgeId));
        setDismissing(prev => { const next = new Set(prev); next.delete(nudgeId); return next; });
      }, 200);
    } catch {
      setDismissing(prev => { const next = new Set(prev); next.delete(nudgeId); return next; });
    }
  };

  const handleAction = (nudge) => {
    setOpen(false);
    if (nudge.seed_prompt) {
      copilot.openWithSeed(nudge.seed_prompt);
    } else if (nudge.action_path) {
      navigate(nudge.action_path);
    } else {
      copilot.open();
    }
  };

  if (!user || nudges.length === 0) return null;

  return (
    <div className="relative" data-testid="copilot-insights-root">
      {/* Trigger pill */}
      <button
        onClick={() => setOpen(p => !p)}
        className="w-full flex items-center justify-between px-3 py-2 bg-amber-500/10 border border-amber-500/30 hover:border-amber-400/60 hover:bg-amber-500/15 text-white transition-colors text-xs group"
        data-testid="copilot-insights-trigger"
      >
        <div className="flex items-center gap-2">
          <Lightbulb className="w-3.5 h-3.5 text-amber-400 group-hover:animate-pulse" />
          <span className="font-medium">Insights</span>
        </div>
        <span className="px-1.5 py-0.5 text-[10px] bg-amber-500 text-black rounded-full font-bold min-w-[18px] text-center" data-testid="copilot-insights-count">
          {nudges.length}
        </span>
      </button>

      {/* Popover */}
      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-[55]" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: 8, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.96 }}
              transition={{ duration: 0.15 }}
              className="fixed bottom-3 left-3 right-3 lg:left-[228px] lg:right-auto lg:bottom-3 lg:w-[340px] bg-obsidian border border-[#2a2a2a] shadow-2xl z-[56] max-h-[60vh] overflow-y-auto"
              data-testid="copilot-insights-popover"
            >
              <div className="sticky top-0 px-3 py-2.5 bg-gradient-to-r from-amber-500/15 to-transparent border-b border-[#222] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Lightbulb className="w-3.5 h-3.5 text-amber-400" />
                  <span className="text-xs font-bold text-white uppercase tracking-wider">Insights</span>
                </div>
                <button onClick={() => setOpen(false)} className="text-gray-500 hover:text-white" aria-label="Close">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>

              <div className="p-2 space-y-1.5">
                {nudges.map(n => {
                  const Icon = severityIcon[n.severity] || Info;
                  const colorClass = severityColor[n.severity] || "text-blue-400";
                  const isDismissing = dismissing.has(n.id);
                  return (
                    <motion.div
                      key={n.id}
                      initial={{ opacity: 1 }}
                      animate={{ opacity: isDismissing ? 0 : 1, x: isDismissing ? 50 : 0 }}
                      className="bg-[#111] border border-[#222] hover:border-crimson/30 transition-colors p-2.5 group"
                      data-testid={`copilot-nudge-${n.id}`}
                    >
                      <div className="flex items-start gap-2">
                        <Icon className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${colorClass}`} />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold text-white leading-snug">{n.title}</div>
                          <div className="text-[11px] text-gray-400 mt-0.5 leading-snug">{n.body}</div>
                          {n.action_label && (
                            <button
                              onClick={() => handleAction(n)}
                              className="mt-2 inline-flex items-center gap-1 text-[11px] text-crimson hover:text-white font-medium transition-colors"
                              data-testid={`copilot-nudge-action-${n.id}`}
                            >
                              {n.seed_prompt ? <Sparkles className="w-3 h-3" /> : null}
                              <span>{n.action_label}</span>
                              <ArrowRight className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                        <button
                          onClick={(e) => dismiss(e, n.id)}
                          className="opacity-0 group-hover:opacity-100 text-gray-600 hover:text-gray-300 transition-opacity shrink-0"
                          aria-label="Dismiss"
                          data-testid={`copilot-nudge-dismiss-${n.id}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CoPilotInsights;
